import { Fragment, ReactNode, useState } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Checkbox, Collapse } from '@mui/material'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import { ITableColumn } from '../TableHead'
import type { DataTableSelection } from '..'
import { columnDividerSx } from '../columnDivider'

export interface ColumnDefinition<T> {
  id: string
  label: string
  align?: 'right' | 'left' | 'center'
  minWidth?: number
  format?: (value: unknown) => string
  render?: (value: unknown, row: T) => ReactNode
}

interface TableBodyProps<T extends { [key: string]: string | number | ReactNode }> {
  visibleRows: T[]
  columns: ITableColumn<T>[]
  handleRowClick?: (user: T) => void
  renderNestedContent?: (row: T) => ReactNode
  selection?: DataTableSelection<T>
}

export default function Component<T extends { [key: string]: string | number | ReactNode }>({
  visibleRows,
  columns,
  handleRowClick,
  renderNestedContent,
  selection,
}: TableBodyProps<T>) {
  const [expandedRow, setExpandedRow] = useState<T | null>(null)

  const render = (column: ITableColumn<T>, row: T): ReactNode => {
    let value = row[column.key as keyof T]
    if (value === 'Unknown') {
      value = '' as T[keyof T]
    }
    if (column.render) {
      return column.render(value, row)
    } else if (column.format) {
      return column.format(value)
    } else {
      return value as ReactNode
    }
  }

  const toggleRow = (id: string) => {
    if (!selection) return
    const next = new Set(selection.selected)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    selection.onChange(next)
  }

  return (
    <TableBody>
      {visibleRows &&
        visibleRows?.map((row, index) => {
          const isExpanded = expandedRow === row
          const rowId = selection?.getRowId(row)
          const isSelected = !!rowId && !!selection?.selected.has(rowId)

          return (
            <Fragment key={index}>
              <TableRow
                hover
                selected={isSelected}
                aria-expanded={renderNestedContent ? isExpanded : undefined}
                sx={{ cursor: handleRowClick || renderNestedContent ? 'pointer' : 'default' }}
                onClick={
                  handleRowClick || renderNestedContent
                    ? () => {
                        handleRowClick?.(row)
                        if (renderNestedContent) {
                          setExpandedRow(currentRow => (currentRow === row ? null : row))
                        }
                      }
                    : undefined
                }
              >
                {selection && (
                  <TableCell
                    padding="checkbox"
                    sx={{ paddingLeft: '12px' }}
                    // Ticking a row should not also count as clicking it.
                    onClick={event => event.stopPropagation()}
                  >
                    {rowId && (
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleRow(rowId)}
                        inputProps={{
                          'aria-label': selection.getRowLabel
                            ? `Select ${selection.getRowLabel(row)}`
                            : 'Select row',
                        }}
                      />
                    )}
                  </TableCell>
                )}
                {renderNestedContent && (
                  <TableCell
                    sx={{
                      padding: '6px 0 6px 16px',
                      width: '40px',
                      height: '40px',
                    }}
                  >
                    <KeyboardArrowDownIcon
                      aria-hidden
                      sx={{
                        color: 'text.secondary',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: theme =>
                          theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                          }),
                      }}
                    />
                  </TableCell>
                )}
                {columns.map((column, index) => (
                  <TableCell
                    key={index}
                    align={column.align}
                    sx={{
                      padding: '6px 12px',
                      paddingLeft: index === 0 ? '24px' : undefined,
                      height: '40px',
                      width: column.width,
                      ...columnDividerSx('#EAECF0'),
                      // Long values wrap within the column rather than widening it.
                      ...(column.width ? { overflowWrap: 'anywhere' } : {}),
                    }}
                  >
                    {render(column, row)}
                  </TableCell>
                ))}
              </TableRow>
              {renderNestedContent && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1 + (selection ? 1 : 0)}
                    sx={{ padding: 0, borderBottom: 0 }}
                  >
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box>{renderNestedContent(row)}</Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          )
        })}
    </TableBody>
  )
}
